<template>
  <div class="mt-10 pb-6" :class="showDivider && 'border-b border-gray-20'">
    <div class="relative">
      <search-icon class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-50 icon-m" />
      <input
        v-model.trim="inputValue"
        type="text"
        class="h-12 w-full rounded-lg pl-10 text-sm text-gray-80 placeholder:text-gray-50 focus:outline-none"
        :class="isAiMode ? 'ai-mode-input border border-transparent pr-36' : 'border border-gray-30 pr-28'"
        :placeholder="
          isAiMode ? $t('conversations.filters.ai_search.placeholder') : $t('filter.groups.search_placeholder')
        "
        @keydown.enter.prevent="isAiMode && submit()"
      />
      <div class="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1.5">
        <button
          v-if="isAiMode && inputValue"
          type="button"
          :aria-label="$t('conversations.filters.ai_search.clear')"
          class="flex h-6 w-6 items-center justify-center rounded-full text-gray-50 transition-colors hover:text-gray-70"
          @click="inputValue = ''"
        >
          <x-icon class="icon-s" />
        </button>
        <button
          type="button"
          role="switch"
          :aria-checked="isAiMode"
          class="flex items-center gap-1 rounded-full px-3 py-2 text-sm font-medium transition-colors"
          :class="
            isAiMode
              ? 'ai-mode-button text-white'
              : 'border border-gray-30 bg-white text-gray-70 hover:border-gray-40 hover:text-gray-80'
          "
          @click="toggleMode"
        >
          {{ $t('conversations.filters.ai_search.ai_mode') }}
        </button>
      </div>
    </div>
    <ai-filter-options
      v-if="isAiMode"
      ref="aiFilterOptionsRef"
      :options="options"
      :autoqa-fields="autoqaFields"
      :spotlight-fields="spotlightFields"
      :prompt="inputValue"
      @ai-results="$emit('ai-results', $event)"
      @use-suggestion="inputValue = $event"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { SearchIcon, XIcon } from '@klausapp/ui-kit/icons'

import { type DefaultFilterOption } from '../../api/filters'
import { type DeconstructedOption } from '../../utils/filters'
import AiFilterOptions from './AiFilterOptions.vue'

defineProps<{
  options: DefaultFilterOption[]
  autoqaFields: DefaultFilterOption[]
  spotlightFields: DefaultFilterOption[]
  showDivider?: boolean
}>()

const emit = defineEmits<{
  (e: 'ai-results', value: DeconstructedOption[]): void
  (e: 'update:search', value: string): void
}>()

// Single source of truth for the mode lives in the parent (FilterModalV2)
const isAiMode = defineModel<boolean>('aiMode', { default: true })

const inputValue = ref('')
const aiFilterOptionsRef = ref<InstanceType<typeof AiFilterOptions> | null>(null)

const toggleMode = () => {
  isAiMode.value = !isAiMode.value
}

const submit = () => {
  aiFilterOptionsRef.value?.submit()
}

// Clear the field whenever the mode changes (button toggle or parent-driven)
watch(isAiMode, () => {
  inputValue.value = ''
})

// In simple mode the input drives the condition search; in AI mode it holds the prompt
watch(inputValue, (val) => {
  emit('update:search', isAiMode.value ? '' : val)
})
</script>

<style lang="scss" scoped>
.ai-mode-input {
  background-origin: border-box;
  background-clip: padding-box, border-box;
  border: 1px solid transparent;
  background-image: linear-gradient(white, white), linear-gradient(135deg, #8d59b1 20%, #406cc4 125%);
}

.ai-mode-button {
  background: linear-gradient(135deg, #8d59b1 20%, #406cc4 125%);
}
</style>
